// src/components/Hotels/HotelAIAnalysis.tsx
import React, { useState } from 'react';
import {
  Box,
  Alert,
  Fade,
  alpha,
  Container,
  useTheme,
  Grid,
  Paper,
  Typography,
  LinearProgress,
  Button
} from '@mui/material';
import { Hotel } from '@types';
import HotelAIFollowup from '@features/hotels/components/HotelAIFollowup';
import HotelScenarioAnalysis from '@features/hotels/components/HotelScenarioAnalysis';

// Import components and hooks from the new structure
import{  // Import hooks
  useFinancialDocuments,
  useHotelMetrics,
  useAiAnalysis,
  useAnalysisExecution,
  useDataSourceSelection,
  useFocusAreaSelection
} from '@features/analysis';
import {
  HeaderSection,
  AnalysisConfiguration,
  LoadingState,
  EmptyAnalysisState,
  AnalysisSummary,
  MarketAnalysisCard,
  PropertyAssessmentCard,
  ValuationInsightsCard,
  FinancialAnalysisCard,
  PerformanceMetricsCard,
  RenovationPotentialCard,
  SustainabilityCard,
} from '@features/analysis/components';
interface HotelAIAnalysisProps {
  hotelId: string;
  hotel?: Hotel;
}

const HotelAIAnalysis: React.FC<HotelAIAnalysisProps> = ({ hotelId }) => {
  const theme = useTheme();
  const [error, setError] = useState<string | null>(null);
  const [validateAttempted, setValidateAttempted] = useState(false);

  // Use custom hooks to manage data and state
  const {
    documents: financialDocuments,
    loading: loadingDocuments,
    selectedDocuments,
    setSelectedDocuments
  } = useFinancialDocuments(hotelId);

  const {
    metrics: hotelMetrics,
    loading: loadingMetrics,
    selectedMetrics,
    setSelectedMetrics,
    averageMetrics
  } = useHotelMetrics(hotelId);

  const {
    analysis,
    loading: loadingAnalysis,
    analyzing,
    runAnalysis: executeAnalysis
  } = useAiAnalysis(hotelId);

  // Advanced analysis execution with progress tracking
  const analysisExecution = useAnalysisExecution({
    onAnalysisComplete: (result) => {
      // Handle successful analysis completion
      console.log('Analysis completed successfully:', result);
    },
    onAnalysisError: (error) => {
      setError(error);
    },
    onProgressUpdate: (progress, step) => {
      console.log(`Analysis progress: ${progress}% - ${step}`);
    }
  });

  const {
    dataSources,
    handleDataSourceChange,
    expandedPanels,
    togglePanel
  } = useDataSourceSelection({
    selectedDocuments,
    setSelectedDocuments,
    selectedMetrics,
    setSelectedMetrics
  });

  const { focusAreas, handleFocusAreaChange } = useFocusAreaSelection();

  // Function to run analysis with validation
  const runAnalysis = async () => {
    if (!hotelId) {
      setError('Cannot run analysis without a Hotel ID.');
      return;
    }

    setValidateAttempted(true);

    const hasMarketData = dataSources.includes('market');
    const hasFinancialData = selectedDocuments.length > 0 || selectedMetrics.length > 0;

    if (!hasMarketData && !hasFinancialData) {
      setError('Please select at least one data source for analysis.');
      return;
    }

    setError(null);

    try {
      await analysisExecution.runAnalysis({
        hotelId,
        focusAreas,
        dataSources,
        selectedDocuments,
        selectedMetrics,
      });

      setValidateAttempted(false);
    } catch (error: any) {
      setError(error instanceof Error ? error.message : 'An error occurred while running analysis');
    }
  };

  // Handler for document selection
  const handleDocumentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setSelectedDocuments((prev) => (checked ? [...prev, name] : prev.filter((id) => id !== name)));

    // If selecting a document, make sure 'financial' is added to dataSources
    if (checked && !dataSources.includes('financial')) {
      handleDataSourceChange({ target: { name: 'financial', checked: true } } as React.ChangeEvent<HTMLInputElement>);
    } else if (!checked) {
      // Check if this was the last document and no metrics are selected
      const updatedDocs = checked
        ? [...selectedDocuments, name]
        : selectedDocuments.filter(id => id !== name);

      if (updatedDocs.length === 0 && selectedMetrics.length === 0) {
        handleDataSourceChange({ target: { name: 'financial', checked: false } } as React.ChangeEvent<HTMLInputElement>);
      }
    }

    // Reset validation error if user starts selecting documents
    if (checked && validateAttempted && error === 'Please select at least one data source for analysis.') {
      setError(null);
    }
  };

  // Handler for metric selection
  const handleMetricChange = (event: React.ChangeEvent<HTMLInputElement> | { target: { name: string; checked: boolean; value?: string[] } }) => {
    const { name, checked, value } = event.target;

    // Handle bulk update (when selecting metric type groups)
    if (name === 'bulkUpdate' && Array.isArray(value)) {
      setSelectedMetrics(value);

      // Auto-select financial data source if metrics are selected
      if (value.length > 0 && !dataSources.includes('financial')) {
        handleDataSourceChange({ target: { name: 'financial', checked: true } } as React.ChangeEvent<HTMLInputElement>);
      }
      return;
    }

    // Handle individual metric selection
    setSelectedMetrics((prev) => (checked ? [...prev, name] : prev.filter((id) => id !== name)));

    // If selecting a metric, make sure 'financial' is added to dataSources
    if (checked && !dataSources.includes('financial')) {
      handleDataSourceChange({ target: { name: 'financial', checked: true } } as React.ChangeEvent<HTMLInputElement>);
    } else if (!checked) {
      // If no metrics or documents are selected anymore, remove 'financial' from dataSources
      const updatedMetrics = checked
        ? [...selectedMetrics, name]
        : selectedMetrics.filter(id => id !== name);

      if (updatedMetrics.length === 0 && selectedDocuments.length === 0) {
        handleDataSourceChange({ target: { name: 'financial', checked: false } } as React.ChangeEvent<HTMLInputElement>);
      }
    }

    if (checked && validateAttempted && error === 'Please select at least one data source for analysis.') {
      setError(null);
    }
  };

  if (!hotelId) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Hotel ID is required.
      </Alert>
    );
  }

  const isFetchingInitial = loadingAnalysis && !analysis && !analyzing;
  const isRunningAnalysis = analyzing;

  // Updated loading states with progress tracking
  const isAnalyzing = analysisExecution.isAnalyzing;
  const isLoading = loadingAnalysis && !analysis && !isAnalyzing;

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header Section */}
        <HeaderSection
          analysis={analysis}
          analyzing={analyzing}
          onAnalyze={runAnalysis}
          loading={loadingAnalysis}
        />

        {/* Configuration Steps */}
        <AnalysisConfiguration
          dataSources={dataSources}
          focusAreas={focusAreas}
          onDataSourceChange={handleDataSourceChange}
          onFocusAreaChange={handleFocusAreaChange}
          financialDocuments={financialDocuments}
          selectedDocuments={selectedDocuments}
          onDocumentChange={handleDocumentChange}
          loadingDocuments={loadingDocuments}
          hotelMetrics={hotelMetrics}
          selectedMetrics={selectedMetrics}
          onMetricChange={handleMetricChange}
          loadingMetrics={loadingMetrics}
          expandedPanels={expandedPanels}
          togglePanel={togglePanel}
          averageMetrics={averageMetrics}
          onRunAnalysis={runAnalysis}
          analyzing={isAnalyzing}
        />

        {/* Error Display */}
        {error && (
          <Fade in={!!error}>
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(211,47,47,0.15)', '& .MuiAlert-icon': { color: theme.palette.error.main } }}>
              {error}
            </Alert>
          </Fade>
        )}

        {/* Loading / Initial State */}
        {(isLoading || isAnalyzing) && (
          <LoadingState
            isAnalyzing={isAnalyzing}
          />
        )}

        {/* Enhanced Progress Display for Active Analysis */}
        {isAnalyzing && (
          <Box sx={{ mb: 4 }}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${theme.palette.primary.main}`,
                bgcolor: alpha(theme.palette.primary.main, 0.02)
              }}
            >
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}>
                    Analysis in Progress
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {analysisExecution.analysisStep || 'Processing your request...'}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={analysisExecution.cancelAnalysis}
                  sx={{ ml: 2 }}
                >
                  Cancel
                </Button>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={analysisExecution.analysisProgress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
                      }
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  {Math.round(analysisExecution.analysisProgress)}%
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}

        {/* Placeholder when no analysis exists and not loading/analyzing */}
        {!analysis && !isLoading && !isAnalyzing && (
          <EmptyAnalysisState onAnalyze={runAnalysis} />
        )}

        {/* Analysis Results Display */}
        {analysis && !isAnalyzing && (
          <Fade in={!!analysis && !isAnalyzing} timeout={700}>
            <Box>
              {/* Summary Dashboard */}
              <AnalysisSummary analysis={analysis} />

              {/* Main Analysis Grid */}
              <Grid container spacing={3}>
                {/* Market Analysis Card */}
                <Grid item xs={12} md={6}>
                  <MarketAnalysisCard analysis={analysis} />
                </Grid>

                {/* Property Assessment Card */}
                <Grid item xs={12} md={6}>
                  <PropertyAssessmentCard analysis={analysis} />
                </Grid>

                {/* Valuation Insights Card */}
                <Grid item xs={12}>
                  <ValuationInsightsCard analysis={analysis} />
                </Grid>

                {/* Focus Area Cards - Only show if selected and data exists */}

                {/* Financial Analysis Card - Show if 'financials' focus area selected */}
                {(() => {
                  console.log('🔍 Financial Analysis Card Debug:');
                  console.log('- focusAreas:', focusAreas);
                  console.log('- focusAreas.includes("financials"):', focusAreas.includes('financials'));
                  console.log('- analysis?.financialAnalysis:', !!analysis?.financialAnalysis);
                  console.log('- Full financialAnalysis object:', analysis?.financialAnalysis);
                  return null;
                })()}
                {focusAreas.includes('financials') && analysis?.financialAnalysis && (
                  <Grid item xs={12} lg={6}>
                    <FinancialAnalysisCard analysis={analysis} />
                  </Grid>
                )}

                {/* Performance Metrics Card - Show if 'financials' focus area selected or if data source includes metrics */}
                {(focusAreas.includes('financials') || dataSources.includes('metrics')) && analysis?.performanceMetricsAnalysis && (
                  <Grid item xs={12} lg={6}>
                    <PerformanceMetricsCard analysis={analysis} />
                  </Grid>
                )}

                {/* Renovation Potential Card - Show if 'renovation' focus area selected */}
                {focusAreas.includes('renovation') && analysis?.renovationPotential && (
                  <Grid item xs={12} lg={6}>
                    <RenovationPotentialCard analysis={analysis} />
                  </Grid>
                )}

                {/* Sustainability Card - Show if 'sustainability' focus area selected */}
                {focusAreas.includes('sustainability') && analysis?.sustainabilityAnalysis && (
                  <Grid item xs={12} lg={6}>
                    <SustainabilityCard analysis={analysis} />
                  </Grid>
                )}

                {/* Marketing Strategy Card - Show if 'marketing' focus area selected */}
                {focusAreas.includes('marketing') && analysis?.marketingStrategy && (
                  <Grid item xs={12} lg={6}>
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Marketing Strategy Analysis
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Marketing strategy analysis coming soon...
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Market Position Detailed Analysis - Show if 'market' focus area selected */}
                {focusAreas.includes('market') && analysis?.marketAnalysisDetailed && (
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Detailed Market Analysis
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Detailed market analysis results coming soon...
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Follow-up and Scenario Analysis Components */}
              {analysis?.id && <HotelAIFollowup hotelId={hotelId} analysisId={analysis.id} />}
              <HotelScenarioAnalysis hotelId={hotelId} />
            </Box>
          </Fade>
        )}
      </Box>
    </Container>
  );
};

export default HotelAIAnalysis;
